import type { Command } from 'commander';
import { validateAggregate } from '@wport/core';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { readJsonInput } from '../../../lib/io-helpers';
import { CliError, ExitCode } from '../../../lib/errors';
import { printJson, sanitizeForTerminal } from '../../../lib/output';

interface ValidateFlags {
	file: string;
}

/**
 * `resumes validate` 核心：離線本地驗證，不發請求、不需登入。通過印 `Valid.`（json：`{valid:true}`）；
 * 失敗逐條印 `path: message` 到 stderr（json 模式改印 `{valid:false, issues}` 到 stdout），
 * 最後一律丟 CliError(exit 2) 讓 top-level handler 收斂 exit code —— 同 `jobs batch` 慣例：
 * 結果先印出，再丟錯誤觸發非 0 exit，命令本身不必碰 process.exit。
 */
export function runResumesValidate(ctx: ResolvedContext, filePath: string): void {
	const input = readJsonInput(filePath, { timeoutMs: ctx.timeoutMs });
	const issues = validateAggregate(input);

	if (issues.length === 0) {
		if (ctx.format === 'json') {
			printJson({ valid: true });
		} else {
			process.stdout.write('Valid.\n');
		}
		return;
	}

	if (ctx.format === 'json') {
		printJson({ valid: false, issues });
	} else {
		for (const issue of issues) {
			process.stderr.write(`${sanitizeForTerminal(issue.path)}: ${sanitizeForTerminal(issue.message)}\n`);
		}
	}
	throw new CliError(`${issues.length} validation issue(s) found`, ExitCode.InvalidArgument);
}

export function registerPersonalResumesValidate(parent: Command): void {
	parent
		.command('validate')
		.description('Validate a resume aggregate JSON file locally (offline, no login required)')
		.requiredOption('--file <path>', 'path to a JSON resume aggregate, or "-" for stdin')
		.action((flags: ValidateFlags, command: Command) => {
			const ctx = resolveContext(command);
			runResumesValidate(ctx, flags.file);
		});
}
